---
title: "Candidate statuses and pipeline"
description: "Move candidates through your hiring pipeline in Chosen. Use the default statuses, customize them per org, and understand what Hired locks and what removing a status does."
category: "candidates"
keywords:
  - "candidate statuses"
  - "hiring pipeline stages"
  - "recruiting pipeline"
  - "customize hiring stages"
schema: "HowTo"
nav:
  section: "guides"
  order: 20
  parent: "/guides/candidates"
---

A candidate's status is where they are in your hiring process. You move them by dragging — a row in the [table](/guides/candidates/table-and-boards), a card on the board — and every move is recorded. The default set covers a normal pipeline, and you can change it to match yours.

## The default statuses

A new organization starts with six:

| Status | What it means |
| --- | --- |
| **Applied** | New. You haven't acted on them yet. |
| **Phone screen** | First conversation scheduled or done. |
| **Interview** | In your real interview loop. |
| **Offer** | Offer extended. |
| **Hired** | Accepted the offer. |
| **Rejected** | Not moving forward. |

Most teams find this is enough. Add stages when your process genuinely has more steps — not because an empty column feels tidy.

## Moving a candidate

<Steps>
  <li>In the table, drag a candidate's row to a new status. On the board, drag their card to a new column.</li>
  <li>The move saves immediately. No confirm step.</li>
</Steps>

Every status change is logged on the candidate's timeline — who moved them, when, and from what. So "why is this person in Offer?" always has an answer, and it isn't a guess.

## Customizing the status set

Statuses are set per organization, so the whole team works the same pipeline.

<Steps>
  <li>Go to **Settings → Organization**.</li>
  <li>Find the statuses list. Add a status, rename one, or reorder the set.</li>
  <li>Save. The new set applies everywhere candidates appear.</li>
</Steps>

Adding and renaming are safe — nothing moves, candidates just see the updated labels. Removing a status is the one that needs care.

<Callout type="warning" title="Removing a status moves its candidates somewhere">
A status can't just vanish with people in it. When you remove one, Chosen asks which remaining status its candidates should move to, then merges them all there. That move is bulk and not individually undoable — so before you delete "Phone screen," know that everyone in it is about to land in whatever status you pick.
</Callout>

## Interview stages are per role

The org-wide statuses cover the whole pipeline, but the interview part of it rarely looks the same for two roles. Each job carries its own **interview stages** — they replace the generic Interview column on that role's board with the real process: Recruiter screen, Systems deep-dive, whatever the role actually runs.

<Steps>
  <li>Open a role's board (pick the position first — stages belong to a job).</li>
  <li>Click **Edit stages** above the columns.</li>
  <li>Rename, add, remove, or reorder the interview stages. The fixed statuses show locked — they're shared by every role.</li>
  <li>Save. The board updates immediately.</li>
</Steps>

This is the same interview plan the job dialog manages, so editing it in either place edits the one set. Removing a stage doesn't strand anyone: candidates in it move back to the generic Interview status, and the dialog says so before you save.

## Hired is not just another column

Moving a candidate to **Hired** does one extra thing: it locks them to that job. A hired candidate is done — they're not getting dragged back to Interview, and they won't surface as available for other roles.

That's deliberate. Hired means hired. If you moved someone there by accident, you can still correct it, but treat Hired as a decision and not a casual drag.

## Limits

Statuses describe a candidate's stage for a single job. The same person applying to two roles has a status in each, tracked separately — advancing them for one job doesn't touch the other. Statuses also don't enforce order: nothing stops you dragging Applied straight to Offer if that's a move you actually want to make.
